import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FileText, Calendar, TrendingUp, 
  UserCheck, UserX, LogOut, Home, 
  Newspaper, Megaphone, Dog, Heart
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const isAdmin = localStorage.getItem('isAdmin');
    
    if (!token || !isAdmin) {
      navigate('/yonetim');
      return;
    }
    
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, membersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/pending-members`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/admin/all-members`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setPendingMembers(pendingRes.data);
      setAllMembers(membersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Oturum süresi doldu');
        handleLogout();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveMember = async (userId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/approve-member/${userId}`,
        {},
        getAuthHeaders()
      );
      toast.success('Üye başarıyla onaylandı!');
      fetchData();
    } catch (error) {
      console.error('Error approving member:', error);
      toast.error('Üye onaylanırken hata oluştu');
    }
  };

  const handleRejectMember = async (userId) => {
    const reason = prompt('Ret nedeni:');
    if (!reason) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/reject-member/${userId}`,
        { reason },
        getAuthHeaders()
      );
      toast.success('Üye başvurusu reddedildi');
      fetchData();
    } catch (error) {
      console.error('Error rejecting member:', error);
      toast.error('İşlem sırasında hata oluştu');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('isAdmin');
    navigate('/yonetim');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_f40446d8-d7d2-4086-ac19-be3f085557d2/artifacts/2lbvgq55_Cane%20Corso%20Logo-05.png"
                alt="Logo"
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Yönetim Paneli</h1>
                <p className="text-sm text-gray-600">Cane Corso İçerik Yönetim Sistemi</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => window.open('/', '_blank')}
                variant="outline"
                className="flex items-center"
              >
                <Home size={18} className="mr-2" />
                Siteye Git
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center text-red-600 border-red-600 hover:bg-red-50"
              >
                <LogOut size={18} className="mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Bekleyen</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pending_members || 0}</p>
              </div>
              <UserCheck className="text-yellow-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Üyeler</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_members || 0}</p>
              </div>
              <Users className="text-green-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Köpekler</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_dogs || 0}</p>
              </div>
              <Dog className="text-blue-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Üreticiler</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_breeders || 0}</p>
              </div>
              <TrendingUp className="text-purple-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Etkinlikler</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_events || 0}</p>
              </div>
              <Calendar className="text-red-500" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Makaleler</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_articles || 0}</p>
              </div>
              <FileText className="text-indigo-500" size={32} />
            </div>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="w-full flex flex-wrap justify-start mb-8 bg-white p-2 rounded-lg shadow-md">
            <TabsTrigger value="pending" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              <UserCheck size={18} className="mr-2" />
              Bekleyen Üyeler ({pendingMembers.length})
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              <Users size={18} className="mr-2" />
              Tüm Üyeler
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              <FileText size={18} className="mr-2" />
              İçerik Yönetimi
            </TabsTrigger>
          </TabsList>

          {/* Pending Members */}
          <TabsContent value="pending">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Bekleyen Üyelik Başvuruları</h2>
              {pendingMembers.length === 0 ? (
                <p className="text-gray-600 text-center py-8">Bekleyen başvuru bulunmamaktadır.</p>
              ) : (
                <div className="space-y-4">
                  {pendingMembers.map((member) => (
                    <div key={member.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-red-500 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p><strong>E-posta:</strong> {member.email}</p>
                            <p><strong>Telefon:</strong> {member.phone}</p>
                            <p><strong>Cane Corso Var mı:</strong> {member.has_dog ? 'Evet' : 'Hayır'}</p>
                            <p><strong>Nasıl Duydu:</strong> {member.how_heard}</p>
                            <p><strong>Başvuru Tarihi:</strong> {new Date(member.created_at).toLocaleDateString('tr-TR')}</p>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          <Button
                            onClick={() => handleApproveMember(member.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <UserCheck size={18} className="mr-2" />
                            Onayla
                          </Button>
                          <Button
                            onClick={() => handleRejectMember(member.id)}
                            variant="outline"
                            className="border-red-600 text-red-600 hover:bg-red-50"
                          >
                            <UserX size={18} className="mr-2" />
                            Reddet
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* All Members */}
          <TabsContent value="members">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Kayıtlı Üyeler</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Ad Soyad</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">E-posta</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Telefon</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Şehir</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">FCI Üretici</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {allMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{member.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.phone}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.city || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{member.fci_breeder_name || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            member.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {member.is_active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Content Management */}
          <TabsContent value="content">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">İçerik Yönetimi</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <a href="/yonetim/makaleler" className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                  <Newspaper className="text-red-600 mb-4" size={40} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Makaleler</h3>
                  <p className="text-gray-600 text-sm">Makale ekle, düzenle, sil</p>
                  <p className="text-2xl font-bold text-gray-900 mt-4">{stats?.total_articles || 0}</p>
                </a>

                <a href="/yonetim/etkinlikler" className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                  <Calendar className="text-red-600 mb-4" size={40} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Etkinlikler</h3>
                  <p className="text-gray-600 text-sm">Etkinlik ekle, düzenle, sil</p>
                  <p className="text-2xl font-bold text-gray-900 mt-4">{stats?.total_events || 0}</p>
                </a>

                <a href="/yonetim/duyurular" className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                  <Megaphone className="text-red-600 mb-4" size={40} />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Duyurular</h3>
                  <p className="text-gray-600 text-sm">Duyuru ekle, düzenle, sil</p>
                  <p className="text-2xl font-bold text-gray-900 mt-4">-</p>
                </a>
              </div>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  ℹ️ İçerik yönetim sayfaları geliştirme aşamasındadır. Şu anda API endpoints hazır durumda.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
